import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { useAuth } from "../context/AuthContext";
import { UsersThree, Plus, X, Trash, ShieldCheck, Lock, GoogleLogo, Key, WhatsappLogo, PaperPlaneTilt, Info } from "@phosphor-icons/react";

const ROLES = [
  { value: "admin",         label: "Admin",          desc: "Full access including user management" },
  { value: "manager",       label: "Manager",        desc: "Operations oversight, reports & audits" },
  { value: "operator",      label: "Operator",       desc: "Daily dispatch, trips, money on road" },
  { value: "fuel_approver", label: "Fuel approver",  desc: "Reviews and approves fuel requests" },
  { value: "user",          label: "User",           desc: "Standard read/write on operations" },
];

const ROLE_BADGE = {
  admin:         "bg-ps-deep text-white",
  manager:       "bg-ps-blue text-white",
  operator:      "bg-emerald-600 text-white",
  fuel_approver: "bg-lime-500 text-ps-deep",
  user:          "bg-slate-200 text-slate-700",
};

const COUNTRY_OPTIONS = [
  { code: "GH", flag: "🇬🇭", label: "Ghana" },
  { code: "TG", flag: "🇹🇬", label: "Togo"  },
  { code: "BJ", flag: "🇧🇯", label: "Benin" },
];

function CountryChips({ value, onChange, testid }) {
  // value === null/undefined → "all countries" (visual: no chips selected → tinted "all" pill).
  const selected = Array.isArray(value) ? value : [];
  const toggle = (code) => {
    const next = selected.includes(code) ? selected.filter((c) => c !== code) : [...selected, code];
    onChange(next);
  };
  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid={testid}>
      {COUNTRY_OPTIONS.map((o) => {
        const on = selected.includes(o.code);
        return (
          <button
            key={o.code}
            type="button"
            onClick={() => toggle(o.code)}
            data-testid={`${testid}-${o.code.toLowerCase()}`}
            className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold border transition-colors ${
              on ? "bg-ps-blue text-white border-ps-blue" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"
            }`}
          >
            <span>{o.flag}</span>
            <span>{o.label}</span>
          </button>
        );
      })}
      {selected.length === 0 ? (
        <span className="text-[10px] uppercase tracking-wider text-slate-500 ml-1">All countries</span>
      ) : (
        <button
          type="button"
          onClick={() => onChange([])}
          data-testid={`${testid}-clear`}
          className="text-[10px] uppercase tracking-wider text-slate-500 hover:text-ps-blue ml-1 underline"
        >
          reset to all
        </button>
      )}
    </div>
  );
}

const emptyForm = { email: "", name: "", role: "user", password: "", notify_phone: "", allowed_countries: [] };

export default function Users() {
  const { user: me } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const isAdmin = me?.role === "admin";
  const [twilioStatus, setTwilioStatus] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data }, statusRes] = await Promise.all([
        api.get("/admin/users"),
        api.get("/admin/notifications/status").catch(() => ({ data: null })),
      ]);
      setRows(data);
      setTwilioStatus(statusRes.data);
    } catch (e) {
      setErr(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load users");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (isAdmin) load(); }, [isAdmin]);

  if (!isAdmin) {
    return (
      <div data-testid="users-page-forbidden">
        <PageHeader subtitle="Administration" title="Users" />
        <div className="p-8">
          <div className="border border-amber-300 bg-amber-50 text-amber-900 rounded-md p-4 text-sm">
            Only admins can manage user accounts. Signed in as <span className="font-semibold">{me?.email}</span> ({me?.role}).
          </div>
        </div>
      </div>
    );
  }

  const submitNew = async (e) => {
    e.preventDefault();
    setErr("");
    setSaving(true);
    try {
      const payload = { ...form, email: form.email.trim().toLowerCase(), name: form.name.trim() };
      if (!payload.password) delete payload.password;
      await api.post("/admin/users", payload);
      setForm(emptyForm);
      setShowNew(false);
      await load();
    } catch (e) {
      setErr(formatApiErrorDetail(e?.response?.data?.detail) || "Could not create user");
    } finally {
      setSaving(false);
    }
  };

  const changeRole = async (row, role) => {
    if (role === row.role) return;
    try {
      await api.patch(`/admin/users/${row.id}`, { role });
      setRows((rs) => rs.map((r) => (r.id === row.id ? { ...r, role } : r)));
    } catch (e) {
      setErr(formatApiErrorDetail(e?.response?.data?.detail) || "Could not change role");
    }
  };

  const resetPassword = async (row) => {
    const pw = window.prompt(`Set a new password for ${row.email}`, "");
    if (!pw) return;
    try {
      await api.patch(`/admin/users/${row.id}`, { password: pw });
      await load();
    } catch (e) {
      setErr(formatApiErrorDetail(e?.response?.data?.detail) || "Could not reset password");
    }
  };

  const editPhone = async (row) => {
    const ph = window.prompt(
      `WhatsApp number for ${row.email}\nFormat: +<country><number> (e.g. +31610306302)\nLeave blank to clear.`,
      row.notify_phone || ""
    );
    if (ph === null) return;
    try {
      await api.patch(`/admin/users/${row.id}`, { notify_phone: ph });
      await load();
    } catch (e) {
      setErr(formatApiErrorDetail(e?.response?.data?.detail) || "Could not update phone");
    }
  };

  const sendTest = async (row) => {
    if (!row.notify_phone) {
      setErr(`${row.email} has no WhatsApp number set yet.`);
      return;
    }
    try {
      const { data } = await api.post("/admin/notifications/test", {
        phone: row.notify_phone,
        message: `✅ Portside test for ${row.name || row.email}`,
      });
      if (data?.ok) {
        setErr("");
        window.alert(`Sent ✓\nTwilio SID: ${data.sid}\nStatus: ${data.status}`);
      } else {
        setErr(data?.error || data?.skipped || "Send failed");
      }
    } catch (e) {
      setErr(formatApiErrorDetail(e?.response?.data?.detail) || "Send failed");
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete user ${row.email}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${row.id}`);
      setRows((rs) => rs.filter((r) => r.id !== row.id));
    } catch (e) {
      setErr(formatApiErrorDetail(e?.response?.data?.detail) || "Could not delete user");
    }
  };

  const changeCountries = async (row, next) => {
    try {
      const payload = { allowed_countries: next };  // [] resets to "all"
      const { data } = await api.patch(`/admin/users/${row.id}`, payload);
      setRows((rs) => rs.map((r) => (r.id === row.id ? { ...r, allowed_countries: data.allowed_countries || null } : r)));
    } catch (e) {
      setErr(formatApiErrorDetail(e?.response?.data?.detail) || "Could not update country access");
    }
  };

  return (
    <div data-testid="users-page">
      <PageHeader
        subtitle="Administration · Access control"
        title="Users"
        action={
          <button
            onClick={() => { setShowNew(true); setErr(""); }}
            data-testid="users-new-button"
            className="bg-ps-blue text-white font-semibold px-4 py-2.5 rounded-md hover:bg-ps-deep transition-colors flex items-center gap-2 text-sm shadow-sm"
          >
            <Plus size={16} weight="bold" />
            Add user
          </button>
        }
      />

      {err && (
        <div className="mx-8 mt-4 border border-red-300 bg-red-50 text-red-700 text-sm px-3 py-2 rounded-md flex items-start justify-between" data-testid="users-error">
          <span>{err}</span>
          <button onClick={() => setErr("")} className="ml-3 text-red-500 hover:text-red-700"><X size={14} /></button>
        </div>
      )}

      <div className="p-8">
        {/* Twilio status banner */}
        {twilioStatus && (
          <div
            className={`mb-4 border rounded-md px-4 py-3 text-sm flex items-start gap-3 ${
              twilioStatus.configured
                ? "bg-emerald-50 border-emerald-200 text-emerald-900"
                : "bg-slate-50 border-slate-200 text-slate-600"
            }`}
            data-testid="twilio-status-banner"
          >
            <WhatsappLogo size={18} weight="duotone" className={twilioStatus.configured ? "text-emerald-600" : "text-slate-400"} />
            <div className="flex-1">
              {twilioStatus.configured ? (
                <>
                  <div className="font-semibold">WhatsApp notifications enabled</div>
                  <div className="text-xs mt-0.5 text-emerald-800">
                    Sender: <span className="font-mono-data">{twilioStatus.from}</span> · Recipients need to join the sandbox once (see admin notes below).
                  </div>
                </>
              ) : (
                <>
                  <div className="font-semibold">WhatsApp notifications not configured</div>
                  <div className="text-xs mt-0.5">Set TWILIO_ACCOUNT_SID, TWILIO_AUTH_TOKEN and TWILIO_WHATSAPP_FROM in the backend .env to enable.</div>
                </>
              )}
            </div>
          </div>
        )}

        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-6 py-3 border-b border-slate-200 flex items-center gap-2">
            <UsersThree size={16} weight="duotone" className="text-ps-blue" />
            <div className="text-[11px] uppercase tracking-[0.2em] text-ps-blue font-semibold">All accounts</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 ml-auto">{rows.length} total</div>
          </div>

          {loading ? (
            <div className="p-8"><div className="indus-bar w-1/3" /></div>
          ) : (
            <table className="w-full text-sm" data-testid="users-table">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-[0.2em] text-slate-600 border-b border-slate-200">
                <tr>
                  <th className="px-3 py-2 text-left">Email</th>
                  <th className="px-3 py-2 text-left">Name</th>
                  <th className="px-3 py-2 text-left w-40">Role</th>
                  <th className="px-3 py-2 text-left w-56">Country access</th>
                  <th className="px-3 py-2 text-left w-28">Auth</th>
                  <th className="px-3 py-2 text-left w-44">WhatsApp</th>
                  <th className="px-3 py-2 text-right w-40">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.length === 0 && (
                  <tr><td colSpan={7} className="p-8 text-center text-slate-500">No users yet.</td></tr>
                )}
                {rows.map((r) => {
                  const isSelf = r.id === me?.id;
                  return (
                    <tr key={r.id} className="hover:bg-slate-50" data-testid={`users-row-${r.email}`}>
                      <td className="px-3 py-2 font-mono-data text-ps-deep">
                        {r.email}
                        {isSelf && <span className="ml-2 text-[10px] uppercase tracking-wider text-ps-blue font-semibold">You</span>}
                      </td>
                      <td className="px-3 py-2 text-slate-700">{r.name || <span className="italic text-slate-400">—</span>}</td>
                      <td className="px-3 py-2">
                        <select
                          value={r.role}
                          disabled={isSelf}
                          onChange={(e) => changeRole(r, e.target.value)}
                          data-testid={`users-role-${r.email}`}
                          className={`text-xs font-semibold rounded px-2 py-1 border border-transparent focus:border-ps-blue ${ROLE_BADGE[r.role] || ROLE_BADGE.user} ${isSelf ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
                        >
                          {ROLES.map((o) => <option key={o.value} value={o.value} className="bg-white text-slate-700">{o.label}</option>)}
                        </select>
                      </td>
                      <td className="px-3 py-2">
                        {r.role === "admin" ? (
                          <span className="text-[10px] uppercase tracking-wider text-ps-deep bg-slate-100 px-2 py-1 rounded font-semibold" title="Admins always see all countries">
                            All (admin)
                          </span>
                        ) : (
                          <CountryChips
                            value={r.allowed_countries || []}
                            onChange={(next) => changeCountries(r, next)}
                            testid={`users-countries-${r.email}`}
                          />
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1.5 text-xs text-slate-600">
                          {r.has_password && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-100 text-slate-700" title="Email/password"><Lock size={11} /> Password</span>}
                          {r.auth_provider === "google" && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-ps-blue/10 text-ps-blue" title="Google SSO"><GoogleLogo size={11} weight="bold" /> Google</span>}
                          {!r.has_password && r.auth_provider !== "google" && <span className="italic text-slate-400">—</span>}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        {r.notify_phone ? (
                          <span className="inline-flex items-center gap-1.5 text-xs font-mono-data text-emerald-700" data-testid={`users-phone-${r.email}`}>
                            <WhatsappLogo size={13} weight="bold" />
                            {r.notify_phone}
                          </span>
                        ) : (
                          <span className="text-xs text-slate-400 italic">Not set</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button
                          onClick={() => editPhone(r)}
                          data-testid={`users-phone-edit-${r.email}`}
                          title="Set / edit WhatsApp number"
                          className="text-slate-500 hover:text-emerald-600 p-1.5 rounded hover:bg-slate-100"
                        >
                          <WhatsappLogo size={14} />
                        </button>
                        <button
                          onClick={() => sendTest(r)}
                          disabled={!r.notify_phone || !twilioStatus?.configured}
                          data-testid={`users-phone-test-${r.email}`}
                          title={r.notify_phone ? "Send test WhatsApp" : "No phone set"}
                          className={`p-1.5 rounded ml-0.5 ${(!r.notify_phone || !twilioStatus?.configured) ? "text-slate-300 cursor-not-allowed" : "text-emerald-600 hover:text-white hover:bg-emerald-600"}`}
                        >
                          <PaperPlaneTilt size={14} />
                        </button>
                        <button
                          onClick={() => resetPassword(r)}
                          data-testid={`users-reset-${r.email}`}
                          title="Set / reset password"
                          className="text-slate-500 hover:text-ps-blue p-1.5 rounded hover:bg-slate-100 ml-0.5"
                        >
                          <Key size={14} />
                        </button>
                        <button
                          onClick={() => remove(r)}
                          disabled={isSelf}
                          data-testid={`users-delete-${r.email}`}
                          title={isSelf ? "Cannot delete yourself" : "Delete user"}
                          className={`p-1.5 rounded ml-0.5 ${isSelf ? "text-slate-300 cursor-not-allowed" : "text-red-500 hover:text-white hover:bg-red-500"}`}
                        >
                          <Trash size={14} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="mt-4 text-xs text-slate-500 flex items-start gap-2 max-w-3xl">
          <ShieldCheck size={14} className="text-ps-blue mt-0.5" />
          <span>
            New accounts are created by admins only. Any user with a <span className="font-semibold text-ps-deep">@portside.nl</span> Google account can also sign in directly — their record is auto-provisioned with role <span className="font-semibold">user</span> the first time they sign in.
          </span>
        </div>

        {twilioStatus?.configured && (
          <div className="mt-3 text-xs text-slate-500 flex items-start gap-2 max-w-3xl" data-testid="twilio-sandbox-hint">
            <Info size={14} className="text-emerald-600 mt-0.5" />
            <span>
              <span className="font-semibold text-slate-700">Sandbox opt-in:</span> {twilioStatus.sandbox_join_instructions}
            </span>
          </div>
        )}
      </div>

      {/* Create modal */}
      {showNew && (
        <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center p-4" data-testid="users-new-modal">
          <form
            onSubmit={submitNew}
            className="bg-white rounded-xl shadow-xl w-full max-w-md border border-slate-200"
          >
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-ps-blue font-semibold">Administration</div>
                <h2 className="font-display font-extrabold text-xl text-ps-deep tracking-tight">Add user</h2>
              </div>
              <button type="button" onClick={() => setShowNew(false)} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
            </div>

            <div className="p-5 space-y-4">
              <div>
                <label className="block text-[11px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1">Email</label>
                <input
                  type="email" required
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  data-testid="users-form-email"
                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                  placeholder="someone@portside.nl"
                />
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1">Display name</label>
                <input
                  type="text" required
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  data-testid="users-form-name"
                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1">Role</label>
                <select
                  value={form.role}
                  onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
                  data-testid="users-form-role"
                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm bg-white"
                >
                  {ROLES.map((o) => <option key={o.value} value={o.value}>{o.label} — {o.desc}</option>)}
                </select>
              </div>
              {form.role !== "admin" && (
                <div>
                  <label className="block text-[11px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1">Country access <span className="text-slate-400 normal-case tracking-normal">(leave empty for all countries)</span></label>
                  <CountryChips
                    value={form.allowed_countries}
                    onChange={(next) => setForm((f) => ({ ...f, allowed_countries: next }))}
                    testid="users-form-countries"
                  />
                </div>
              )}
              <div>
                <label className="block text-[11px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1">Password <span className="text-slate-400 normal-case tracking-normal">(optional — leave blank for Google-only sign-in)</span></label>
                <input
                  type="text"
                  value={form.password}
                  onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                  data-testid="users-form-password"
                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm font-mono-data"
                  placeholder="Leave empty to require Google sign-in"
                />
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1 flex items-center gap-1.5">
                  <WhatsappLogo size={13} weight="bold" className="text-emerald-600" />
                  WhatsApp number <span className="text-slate-400 normal-case tracking-normal">(optional · for fuel-request alerts)</span>
                </label>
                <input
                  type="tel"
                  value={form.notify_phone}
                  onChange={(e) => setForm((f) => ({ ...f, notify_phone: e.target.value }))}
                  data-testid="users-form-phone"
                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm font-mono-data"
                  placeholder="+31610306302"
                />
              </div>
            </div>

            <div className="px-5 py-4 border-t border-slate-200 flex items-center justify-end gap-2 bg-slate-50 rounded-b-xl">
              <button type="button" onClick={() => setShowNew(false)} className="px-4 py-2 text-sm rounded-md text-slate-600 hover:bg-slate-200">Cancel</button>
              <button
                type="submit"
                disabled={saving}
                data-testid="users-form-submit"
                className="bg-ps-blue text-white font-semibold px-4 py-2 rounded-md hover:bg-ps-deep transition-colors text-sm disabled:opacity-50"
              >
                {saving ? "Creating…" : "Create user"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
